import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Users, Package, Receipt, BarChart3, Truck, Boxes, Webhook, Unlink, LogOut, ChevronDown, Globe, Gift } from "lucide-react";
import { clearSession } from "@/lib/auth";
import { isOwner } from "@/lib/useMerchant";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * Initials avatar — placeholder until we're pulling the real Instagram
 * profile picture. Doing that requires the OAuth callback workflow to
 * capture profile_picture_url from the IG Graph API and api-me to return
 * it; not wired yet, so this is the interim.
 */
function Avatar({ username }) {
  const initials = (username || "?").slice(0, 2).toUpperCase();
  return (
    <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-sunset-gradient text-sm font-bold text-white">
      {initials}
    </span>
  );
}

// Matches the confirm/decision dialog pattern already used in Dashboard.jsx
// (order confirm/reject, return decision) — centered card over a dimmed
// backdrop, instead of the browser's native window.confirm().
//
// Portaled to document.body on purpose: this component lives inside
// <DashboardHeader>, whose <header> has `backdrop-blur` (backdrop-filter).
// Per the CSS spec, any ancestor with a filter/backdrop-filter/transform
// becomes the containing block for `position: fixed` descendants — so
// without the portal, this dialog ends up "fixed" relative to the header
// instead of the viewport, which is why it rendered pinned near the top
// and cut off instead of centered on screen.
function DisconnectDialog({ open, username, isSubmitting, onCancel, onConfirm }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
        <span className="mx-auto grid h-11 w-11 place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
          <Unlink size={18} />
        </span>
        <p className="mt-3 font-display text-xl font-black tracking-tight">
          Disconnect Instagram?
        </p>
        <p className="mt-2 text-sm text-ink/60">
          {username ? `@${username} ` : "Your account "}will stop responding to DMs until you reconnect. Existing chats, orders, and customer data are kept — nothing gets deleted.
        </p>
        <div className="mt-5 flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="disconnect-dialog-cancel"
            className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            data-testid="disconnect-dialog-confirm"
            className="flex-1 rounded-full bg-hotpink py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Disconnecting…" : "Yes, disconnect"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ProfileMenu({ me }) {
  const navigate = useNavigate();
  const owner = isOwner(me);
  const [igConnected, setIgConnected] = useState(true);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    // GET /profile is owner-only server-side now — team members would just
    // get a 403 here, so skip the call entirely rather than eat a wasted
    // request (and a console error) on every member page load.
    if (!owner) return;
    api
      .get("/profile")
      .then(({ data }) => setIgConnected(!!data.instagram_connected))
      .catch(() => {});
  }, [owner]);

  const handleLogout = () => {
    clearSession();
    toast.success("Signed out.");
    navigate("/");
  };

  const confirmDisconnect = async () => {
    if (isDisconnecting) return;
    setIsDisconnecting(true);
    try {
      await api.post("/instagram/disconnect");
      toast.success("Instagram disconnected.");
      window.location.href = "/dashboard";
    } catch {
      toast.error("Couldn't disconnect. Try again.");
      setIsDisconnecting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-menu-trigger"
            className="flex items-center gap-2 rounded-full border border-ink/10 bg-white py-1 pl-1 pr-3 hover:bg-ink/5"
          >
            <Avatar username={me?.username} />
            <span className="hidden text-sm font-semibold md:inline">
              {me?.username ? `@${me.username}` : "Account"}
            </span>
            <ChevronDown size={14} className="text-ink/50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-white">
          <div className="px-2 py-1.5">
            <p className="text-sm font-semibold text-ink">
              {me?.username ? `@${me.username}` : "Your account"}
            </p>
            {me?.plan_meta?.name || me?.plan ? (
              <p className="text-xs text-ink/50">
                {me?.plan_meta?.name || (me?.plan ? me.plan.charAt(0).toUpperCase() + me.plan.slice(1) : "")} plan
              </p>
            ) : null}
          </div>
          <DropdownMenuSeparator />
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/profile")}
              data-testid="menu-profile"
              className="cursor-pointer"
            >
              <User size={14} className="mr-2" /> Profile
            </DropdownMenuItem>
          )}
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/team")}
              data-testid="menu-team"
              className="cursor-pointer"
            >
              <Users size={14} className="mr-2" /> Team
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => navigate("/catalog")}
            data-testid="menu-catalog"
            className="cursor-pointer"
          >
            <Package size={14} className="mr-2" /> Catalog
          </DropdownMenuItem>
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/public-catalog")}
              data-testid="menu-public-catalog"
              className="cursor-pointer"
            >
              <Globe size={14} className="mr-2" /> Storefront
            </DropdownMenuItem>
          )}
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/billing")}
              data-testid="menu-billing"
              className="cursor-pointer"
            >
              <Receipt size={14} className="mr-2" /> Billing
            </DropdownMenuItem>
          )}
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/billing")}
              data-testid="menu-referral"
              className="cursor-pointer"
            >
              <Gift size={14} className="mr-2" /> Refer &amp; earn
            </DropdownMenuItem>
          )}
          {owner && me?.plan !== "creator" && (
            <DropdownMenuItem
              onClick={() => navigate("/shipping")}
              data-testid="menu-shipping"
              className="cursor-pointer"
            >
              <Truck size={14} className="mr-2" /> Shipping & Discounts
            </DropdownMenuItem>
          )}
          {owner && me?.plan !== "creator" && (
            <DropdownMenuItem
              onClick={() => navigate("/courier")}
              data-testid="menu-courier"
              className="cursor-pointer"
            >
              <Boxes size={14} className="mr-2" /> Courier
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => navigate("/analytics")}
            data-testid="menu-analytics"
            className="cursor-pointer"
          >
            <BarChart3 size={14} className="mr-2" /> Analytics
          </DropdownMenuItem>
          {owner && (
            <DropdownMenuItem
              onClick={() => navigate("/webhooks")}
              data-testid="menu-webhooks"
              className="cursor-pointer"
            >
              <Webhook size={14} className="mr-2" /> Webhooks
            </DropdownMenuItem>
          )}
          {owner && (
            <DropdownMenuItem
              onClick={igConnected ? () => setDisconnectOpen(true) : undefined}
              disabled={!igConnected}
              data-testid="menu-disconnect-instagram"
              className={
                igConnected
                  ? "cursor-pointer text-hotpink focus:text-hotpink"
                  : "cursor-not-allowed text-ink/30"
              }
            >
              <Unlink size={14} className="mr-2" />
              {igConnected ? "Disconnect Instagram" : "Instagram disconnected"}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            data-testid="menu-logout"
            className="cursor-pointer text-hotpink focus:text-hotpink"
          >
            <LogOut size={14} className="mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DisconnectDialog
        open={disconnectOpen}
        username={me?.username}
        isSubmitting={isDisconnecting}
        onCancel={() => setDisconnectOpen(false)}
        onConfirm={confirmDisconnect}
      />
    </>
  );
}
